import React from 'react';
import ReactDOM from 'react-dom/client';
import './index.css';
import './i18n/config'; // Initialize i18n
import App from './App';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { HelmetProvider } from 'react-helmet-async';
import { AppErrorBoundary } from './components/common/ErrorBoundaryWrappers';
import { initializeOptimizations } from './utils/assetOptimizations';
import { initGA } from './utils/analytics';
import { PRODUCT_NAME } from './config/runtimeConfig';
import { applyRuntimeDocumentIdentity } from './config/runtimeDocumentIdentity';

applyRuntimeDocumentIdentity(PRODUCT_NAME);

// Optimize React Query configuration for better performance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: (failureCount, error) => {
        // Don't retry on 401/403 errors
        if (error?.response?.status === 401 || error?.response?.status === 403) {
          return false;
        }
        return failureCount < 3;
      },
    },
    mutations: {
      retry: 1,
    },
  },
});

// Performance monitoring
const reportWebVitals = (metric) => {
  // In production, you could send this to an analytics service
  if (process.env.NODE_ENV === 'development') {
    // Development-only performance monitoring
    // Removed console.log for production security
  }
};

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
  <React.StrictMode>
    <AppErrorBoundary>
      <HelmetProvider>
        <BrowserRouter>
          <QueryClientProvider client={queryClient}>
            <App />
          </QueryClientProvider>
        </BrowserRouter>
      </HelmetProvider>
    </AppErrorBoundary>
  </React.StrictMode>
);

// Initialize performance optimizations
initializeOptimizations();

// Initialize Google Analytics
initGA();

// Report web vitals for performance monitoring
if ('web-vitals' in window || process.env.NODE_ENV === 'development') {
  import('web-vitals').then(({ getCLS, getFID, getFCP, getLCP, getTTFB }) => {
    getCLS(reportWebVitals);
    getFID(reportWebVitals);
    getFCP(reportWebVitals);
    getLCP(reportWebVitals);
    getTTFB(reportWebVitals);
  }).catch(() => {
    // Gracefully handle if web-vitals is not available
  });
}